import type { FeatureCollection, Point } from 'geojson';
import type { LngLat } from 'mapbox-gl';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import {
  clusterCasingLayer,
  clusterCountLayer,
  clusterLayer,
  symbolPointLayer,
  unclusteredPointCasingLayer,
  unclusteredPointLayer,
} from './cluster.js';

interface Props {
  id: string;
  onClick?: (latLng: LngLat, props: PointProperties) => void;
  points: FeatureCollection<Point, PointProperties>;
}

type PointProperties = Record<string, unknown>;

interface PointLayerClickEvent {
  features?: Array<{ properties?: unknown }>;
  lngLat: LngLat;
}

const isPointProperties = (value: unknown): value is PointProperties =>
  !!value && typeof value === 'object';

const isPointLayerClickEvent = (
  value: unknown,
): value is PointLayerClickEvent => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'lngLat' in value;
};

export const PointsLayer = ({
  id,
  onClick,
  points,
}: Props): ReactElement | null => {
  const interactiveLayerId = `unclustered-layer-${id}`;
  const { current: map } = useMap();

  // biome-ignore lint/correctness/useExhaustiveDependencies: working, don't mess with it
  useEffect(() => {
    if (!map) return;
    const onEnter = (): void => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const onLeave = (): void => {
      map.getCanvas().style.cursor = '';
    };

    const onLayerClick = (event: unknown): void => {
      if (!isPointLayerClickEvent(event) || !event.features?.length) {
        return;
      }
      const feature = event.features[0];
      if (!isPointProperties(feature.properties)) {
        return;
      }
      onClick?.(event.lngLat, feature.properties);
    };

    // // When a click event occurs on a feature in the places layer, open a popup at the
    // // location of the feature, with description HTML from its properties.
    // map.on('click', interactiveLayerId, (e) => {
    //   // Copy coordinates array.
    //   const coordinates = e.features[0].geometry.coordinates.slice();
    //   const description = e.features[0].properties.description;

    //   // Ensure that if the map is zoomed out such that multiple
    //   // copies of the feature are visible, the popup appears
    //   // over the copy being pointed to.
    //   while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
    //     coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    //   }

    //   new mapboxgl.Popup().setLngLat(coordinates).setHTML(description).addTo(map);
    // });

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on('mouseenter', interactiveLayerId, onEnter);

    // Change it back to a pointer when it leaves.
    map.on('mouseleave', interactiveLayerId, onLeave);

    // When a click event occurs on a feature in the places layer, open a popup at the
    // location of the feature, with description HTML from its properties.
    map.on('click', interactiveLayerId, onLayerClick);

    return () => {
      map.off('mouseenter', interactiveLayerId, onEnter);
      map.off('mouseleave', interactiveLayerId, onLeave);
      map.off('click', interactiveLayerId, onLayerClick);
    };
  }, [map]);

  return (
    <Source
      cluster={true}
      clusterMaxZoom={16}
      clusterRadius={15}
      data={points}
      id={`point-data-${id}`}
      type="geojson"
    >
      <Layer id={`cluster-casing-layer-${id}`} {...clusterCasingLayer} />
      <Layer id={`cluster-layer-${id}`} {...clusterLayer} />
      <Layer id={`cluster-count-layer-${id}`} {...clusterCountLayer} />
      <Layer
        id={`unclustered-casing-layer-${id}`}
        {...unclusteredPointCasingLayer}
      />
      <Layer id={`unclustered-layer-${id}`} {...unclusteredPointLayer} />
      <Layer id={`symbol-layer-${id}`} {...symbolPointLayer} />
    </Source>
  );
};
